/**
 * UI shape of a club schedule block plus the rules that decide whether it can
 * be offered for a given tournament. Kept apart from ClubSchedulesStep so the
 * rules can be read together, without the step's rendering noise.
 */

import {
  isLocalDateAfter,
  minLocalMidnightDate,
  toLocalMidnightDate,
} from '@/utils/localDateOnly';
import type { KnockoutStart } from '@/utils/tournament-schedule';
import type { Schedule as ApiSchedule, ScheduleTournamentRef } from '@/types/schedule.model';
import { TournamentStatus, type TournamentFormat } from '@/types/tournament.model';

export interface TimeBlock {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  courtCount: number;
}

export interface Schedule {
  id: string;
  title: string;
  clubId: string;
  clubName: string;
  clubAddress: string;
  timeBlocks: TimeBlock[];
  isReusable: boolean;
  createdAt: string;
  /** Tournaments already using this block; drives the compatibility rule. */
  tournaments?: ScheduleTournamentRef[];
  /** Slot from which the block plays knockout (SHORT only); null when it has no cut. */
  knockoutStart: KnockoutStart | null;
}

/** Parse "HH:mm" to minutes since midnight; return duration in minutes. */
function getSlotDurationMinutes(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

/** Filter time blocks to only those whose duration equals matchDuration. */
export function filterTimeBlocksByMatchDuration(
  blocks: TimeBlock[],
  matchDuration: number,
): TimeBlock[] {
  return blocks.filter(
    (b) => getSlotDurationMinutes(b.startTime, b.endTime) === matchDuration,
  );
}

function getScheduleStartDate(schedule: Schedule): Date | null {
  return minLocalMidnightDate(schedule.timeBlocks.map((b) => b.date));
}

export function isScheduleStartAfterDeadline(
  schedule: Schedule,
  signupDeadlineDate: Date | string | null | undefined,
): boolean {
  if (!signupDeadlineDate) return true;
  const start = getScheduleStartDate(schedule);
  if (!start) return false;
  const deadline = toLocalMidnightDate(signupDeadlineDate);
  if (!deadline) return true;
  return isLocalDateAfter(start, deadline) === true;
}

/**
 * A block can only be shared between tournaments of the same sport and format.
 * The block itself stores neither, so both are derived from the tournaments
 * already using it — ignoring CANCELLED ones, same as the backend's sibling
 * query does.
 *
 * Fails open: with no known sport or format, every block is considered
 * compatible. Comparing against undefined would hide the whole list, which is
 * worse than showing too much — the backend validates this too.
 */
export function isScheduleCompatibleWithTournament(
  schedule: Schedule,
  target: {
    sportUid?: string | null;
    format?: TournamentFormat | null;
    tournamentId?: string;
  },
): boolean {
  if (!target.sportUid || !target.format) return true;

  const others = (schedule.tournaments ?? []).filter(
    (t) => t.id !== target.tournamentId && t.status !== TournamentStatus.CANCELLED,
  );

  return others.every((t) => {
    // A tournament we cannot read is skipped rather than treated as a clash.
    if (!t.sport?.uid || !t.format) return true;
    return t.sport.uid === target.sportUid && t.format === target.format;
  });
}

/**
 * Weekly grids a LONG tournament can share: same sport as the tournaments already using it (a grid
 * nobody uses fits any sport) and every slot lasting exactly one match. The backend validates both.
 */
export function isEligibleWeeklyGrid(
  grid: ApiSchedule,
  target: { sportUid?: string | null; matchDuration: number },
): boolean {
  if (!grid.isWeeklyGrid) return false;
  const users = (grid.tournaments ?? []).filter((t) => t.status !== TournamentStatus.CANCELLED);
  const sameSport =
    !target.sportUid || users.every((t) => !t.sport?.uid || t.sport.uid === target.sportUid);
  const ranges = (grid.schedule ?? []).flatMap((entry) => entry.timeRange);
  const oneMatchSlots =
    ranges.length > 0 &&
    ranges.every((r) => getSlotDurationMinutes(r.startTime, r.endTime) === target.matchDuration);
  return sameSport && oneMatchSlots;
}
